<script setup lang="ts">
import { FAB_ORDER } from '~/domains/shared/components/fab-action.constants'
import FabAction from '~/domains/shared/components/FabAction.vue'
import { useBOC } from '../composables/useBOC'
import { operationStatus } from '../utils/lifecycle'
import BOCPanel from './BOCPanel.vue'

const { t } = useI18n()
const { active, recent, workingCount, now, dismiss } = useBOC()

const visible = computed(() => active.value.length > 0 || recent.value.length > 0)

// `recent` is sliced to RECENT_LIMIT in useBOC; older failures fall outside
// the panel and so should not light up the button. The pill mirrors what the
// panel surfaces, by design.
const hasFailure = computed(() =>
  recent.value.some(item => operationStatus(item.run) === 'failed'),
)

// The pill animates on compute, not on presence: a resumable run sits under
// "In progress" without spinning the button or being counted as running.
const buttonIcon = computed(() => {
  if (workingCount.value > 0) {
    return 'i-tabler-loader-2'
  }
  if (hasFailure.value) {
    return 'i-tabler-alert-triangle'
  }
  return 'i-tabler-history'
})

const buttonColor = computed<'gray' | 'primary' | 'red'>(() => {
  if (workingCount.value > 0) {
    return 'primary'
  }
  if (hasFailure.value) {
    return 'red'
  }
  return 'gray'
})

const ariaLabel = computed(() =>
  workingCount.value > 0
    ? t('operations.boc.pill.active', { count: workingCount.value })
    : t('operations.boc.title'),
)
</script>

<template>
  <FabAction
    v-if="visible"
    variant="soft"
    :order="FAB_ORDER.boc"
    :icon="buttonIcon"
    :color="buttonColor"
    :icon-class="workingCount > 0 ? 'animate-spin' : ''"
    :aria-label="ariaLabel"
  >
    <span
      v-if="workingCount > 0"
      class="ml-1 text-sm font-semibold tabular-nums"
    >
      {{ workingCount }}
    </span>

    <template #popover>
      <BOCPanel
        :active="active"
        :recent="recent"
        :now="now"
        @dismiss="dismiss($event)"
      />
    </template>
  </FabAction>
</template>
